import React, { useState } from "react";
import { X, Copy, Check, AlertTriangle, KeyRound } from "lucide-react";

/**
 * Modal one-shot per mostrare in chiaro una password appena creata/resettata.
 * La password è mostrata **una sola volta** — dopo la chiusura non è più recuperabile
 * perché il backend salva solo l'hash bcrypt.
 */
export default function PasswordDisplayModal({ open, onClose, user, password, mode = "reset" }) {
  const [copied, setCopied] = useState(false);
  const [reveal, setReveal] = useState(true);

  if (!open) return null;

  const copyAll = async () => {
    const text = `Portale Adybo Energy Monitor\nEmail: ${user?.email || ""}\nPassword: ${password}\n\nPer motivi di sicurezza cambia la password al primo accesso.`;
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    } catch {
      // fallback
      const ta = document.createElement("textarea");
      ta.value = text;
      document.body.appendChild(ta);
      ta.select();
      document.execCommand("copy");
      ta.remove();
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    }
  };

  const title =
    mode === "create"
      ? "Account creato con successo"
      : mode === "reset-auto"
      ? "Password resettata"
      : "Nuova password impostata";

  return (
    <div
      className="fixed inset-0 z-[70] flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm"
      data-testid="password-display-modal"
    >
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden animate-[fadeIn_0.15s_ease-out]">
        <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between bg-gradient-to-br from-blue-500 to-purple-600 text-white">
          <div className="flex items-center gap-2">
            <KeyRound size={18} />
            <div className="font-display font-semibold">{title}</div>
          </div>
          <button
            onClick={onClose}
            className="p-1 rounded-md hover:bg-white/10"
            aria-label="Chiudi"
            data-testid="password-modal-close"
          >
            <X size={18} />
          </button>
        </div>

        <div className="p-5 space-y-4">
          <div className="flex items-start gap-2 p-3 bg-amber-50 border border-amber-200 rounded-lg">
            <AlertTriangle size={16} className="text-amber-600 flex-shrink-0 mt-0.5" />
            <div className="text-xs text-amber-800 leading-relaxed">
              <strong>Questa password è visibile una sola volta.</strong> Copiala e condividila con l&apos;utente
              tramite un canale sicuro (es. messaggio diretto, non email). Dopo aver chiuso questa finestra
              non sarà più recuperabile.
            </div>
          </div>

          <div>
            <div className="text-[11px] uppercase tracking-wider font-semibold text-slate-500 mb-1">Utente</div>
            <div className="text-sm text-slate-900 font-semibold">{user?.full_name || "—"}</div>
            <div className="text-sm text-slate-600" data-testid="pw-modal-email">{user?.email}</div>
          </div>

          <div>
            <div className="text-[11px] uppercase tracking-wider font-semibold text-slate-500 mb-1">Password</div>
            <div className="flex items-stretch gap-2">
              <div
                className="flex-1 px-3 py-2.5 rounded-lg bg-slate-100 font-mono text-sm text-slate-900 select-all break-all"
                data-testid="pw-modal-value"
                style={{ letterSpacing: reveal ? "0" : "3px" }}
              >
                {reveal ? password : "•".repeat(password?.length || 12)}
              </div>
              <button
                onClick={() => setReveal((v) => !v)}
                className="px-3 rounded-lg border border-slate-200 text-xs text-slate-600 hover:border-slate-400"
                data-testid="pw-modal-toggle-reveal"
              >
                {reveal ? "Nascondi" : "Mostra"}
              </button>
            </div>
          </div>

          <div className="flex items-center gap-2 pt-1">
            <button
              onClick={copyAll}
              className="btn-brand flex items-center gap-2 flex-1 justify-center"
              data-testid="pw-modal-copy"
            >
              {copied ? <Check size={16} /> : <Copy size={16} />}
              {copied ? "Copiato negli appunti" : "Copia email + password"}
            </button>
            <button
              onClick={onClose}
              className="px-4 py-2 rounded-lg border border-slate-200 text-sm font-semibold text-slate-600 hover:border-slate-400"
              data-testid="pw-modal-done"
            >
              Fatto
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
